import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  KeyboardArrowDown as MenuArrowIcon,
} from "@mui/icons-material";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { HighlightedText } from "../searchUtils";
import {
  ColumnRange,
  geometryFor,
  GridSelection,
  insertionIndexAt,
  MAX_RESIZE_WIDTH,
  MIN_COLUMN_WIDTH,
  ROW_HEIGHT,
  ROW_INDEX_WIDTH,
  visibleColumnRange,
} from "./gridGeometry";
import SheetGridRow, { RowItemData } from "./SheetGridRow";
import { ColumnId, CompiledView, RowId } from "./sheetTypes";
import { SortRule } from "./sheetViewState";

/**
 * The virtualised table. Rows are windowed by react-window; columns are
 * windowed here, by hand, once a sheet is wide enough to make it worth it -
 * a Grid would have meant rebuilding the sticky gutter and sticky header that
 * already work, for the same result.
 *
 * Frozen columns are drawn on every row regardless of the window, and the
 * window itself starts after them, so a frozen column is never drawn twice.
 */

interface SheetGridProps {
  view: CompiledView;
  /** Widths measured from the file, indexed by column.sourceIndex. */
  widthBySourceIndex: number[];
  /** Widths set by hand, keyed by column id. */
  columnWidths: Record<ColumnId, number>;
  /** How many leading columns stay put while the rest scrolls. */
  frozenCount: number;
  /** Hidden row ids, ascending - used to draw "rows are hidden here" markers. */
  hiddenRowIds: RowId[];
  /** Markers only make sense while the rows are in file order. */
  showRowGapMarkers: boolean;
  query: string;
  activeMatch: { rowPos: number; colPos: number } | null;
  selection: GridSelection | null;
  sort: SortRule[];
  listRef: React.MutableRefObject<FixedSizeList | null>;
  onSelectionChange: (selection: GridSelection | null) => void;
  onSortColumn: (columnId: ColumnId, additive: boolean) => void;
  onOpenColumnMenu: (columnId: ColumnId, anchor: HTMLElement) => void;
  onRevealRows: (rowIds: RowId[]) => void;
  onRevealColumnsBefore: (columnId: ColumnId) => void;
  onCellExpand: (rowPos: number, colPos: number, anchor: HTMLElement) => void;
  onColumnWidthChange: (columnId: ColumnId, width: number) => void;
  onAutoFitColumn: (columnId: ColumnId) => void;
  /** Put the column immediately before that one; null drops it past the last. */
  onMoveColumn: (columnId: ColumnId, beforeColumnId: ColumnId | null) => void;
}

const HEADER_CELL_BASE = {
  flexShrink: 0,
  p: "6px 8px",
  fontSize: 12,
  fontWeight: 700,
  borderRight: "1px solid",
  borderColor: "divider",
  boxSizing: "border-box" as const,
  overflow: "hidden",
  whiteSpace: "nowrap" as const,
  position: "relative" as const,
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  cursor: "grab",
  // The only hover affordance in the header besides the resize handle. A top
  // accent rather than a grip glyph: a glyph would cost layout width, and
  // HEADER_CONTROLS_WIDTH is baked into every measured column width. The inset
  // keeps it clear of the reveal strip and the resize handle.
  "&:hover": { backgroundColor: "action.hover" },
  "&:hover::before": {
    content: '""',
    position: "absolute" as const,
    top: 0,
    left: 6,
    right: 6,
    height: 2,
    backgroundColor: "primary.light",
  },
};

const RESIZE_HANDLE_WIDTH = 6;

/** How far the pointer has to travel before a press becomes a drag. */
const DRAG_THRESHOLD_PX = 4;
/** How close to an edge the pointer has to get before the grid scrolls. */
const AUTO_SCROLL_EDGE = 48;
const AUTO_SCROLL_MAX_STEP = 24;
const INDICATOR_WIDTH = 2;
/** One hint node for the grid, referenced by every header cell. */
const REORDER_HINT_ID = "sheet-grid-reorder-hint";

const SR_ONLY_SX = {
  position: "absolute" as const,
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap" as const,
  border: 0,
  p: 0,
  m: "-1px",
};

interface ResizeStart {
  columnId: ColumnId;
  startX: number;
  startWidth: number;
}

interface ReorderStart {
  columnId: ColumnId;
  fromPosition: number;
  pointerId: number;
  startX: number;
  startY: number;
  /** The grid box in viewport space, read once - it cannot move mid-drag. */
  rootLeft: number;
  rootWidth: number;
  /** True once the pointer passed the threshold and the press became a drag. */
  armed: boolean;
}

/**
 * How many manually hidden rows sit above each visible row. Filtered-out rows
 * are deliberately not counted: a filter is described by its own chip, while a
 * hidden row has nothing else to announce it.
 */
function hiddenRowsBeforeCounts(
  view: CompiledView,
  hiddenRowIds: RowId[],
  enabled: boolean,
): Record<number, number> {
  const out: Record<number, number> = {};
  if (!enabled || hiddenRowIds.length === 0) return out;

  const hidden = hiddenRowIds.slice().sort((a, b) => a - b);
  let cursor = 0;
  let previous = -1;
  for (let position = 0; position < view.rows.length; position++) {
    const sourceIndex = view.rows[position].sourceIndex;
    let count = 0;
    while (cursor < hidden.length && hidden[cursor] <= previous) cursor++;
    let probe = cursor;
    while (probe < hidden.length && hidden[probe] < sourceIndex) {
      count++;
      probe++;
    }
    if (count > 0) out[position] = count;
    previous = sourceIndex;
    cursor = probe;
  }
  return out;
}

const SheetGrid: React.FC<SheetGridProps> = ({
  view,
  widthBySourceIndex,
  columnWidths,
  frozenCount,
  hiddenRowIds,
  showRowGapMarkers,
  query,
  activeMatch,
  selection,
  sort,
  listRef,
  onSelectionChange,
  onSortColumn,
  onOpenColumnMenu,
  onRevealRows,
  onRevealColumnsBefore,
  onCellExpand,
  onColumnWidthChange,
  onAutoFitColumn,
  onMoveColumn,
}) => {
  const headerScrollRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const scrollLeftRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const resizeRef = useRef<ResizeStart | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<ReorderStart | null>(null);
  const dropIndexRef = useRef(0);
  const pointerXRef = useRef(0);
  const suppressClickRef = useRef(false);
  const autoScrollStepRef = useRef(0);
  const autoScrollFrameRef = useRef<number | null>(null);
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const chipRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [range, setRange] = useState<ColumnRange>({ first: 0, last: -1 });
  const [resizeDraft, setResizeDraft] = useState<{
    columnId: ColumnId;
    width: number;
  } | null>(null);
  /**
   * The two things a reorder drag puts in React state, both of which change
   * once per gesture. Everything that moves per frame - the insertion point,
   * the pointer, the scroll step - lives in refs and is written straight to the
   * DOM, so dragging a column never re-renders a single row.
   */
  const [dragColumn, setDragColumn] = useState<ColumnId | null>(null);
  const [announcement, setAnnouncement] = useState("");

  /**
   * The width being dragged lives here rather than in the view state: a commit
   * per pixel would push every keystroke of a drag through the reducer and the
   * storage debounce.
   */
  const effectiveWidths = useMemo(() => {
    if (!resizeDraft) return columnWidths;
    return { ...columnWidths, [resizeDraft.columnId]: resizeDraft.width };
  }, [columnWidths, resizeDraft]);

  const geometry = useMemo(
    () => geometryFor(view.columns, widthBySourceIndex, effectiveWidths),
    [view.columns, widthBySourceIndex, effectiveWidths],
  );

  const frozen = Math.min(Math.max(frozenCount, 0), view.columns.length);

  const hiddenBefore = useMemo(
    () => view.hiddenColumnsBefore.map((ids) => ids.length),
    [view.hiddenColumnsBefore],
  );

  const hiddenRowsBefore = useMemo(
    () => hiddenRowsBeforeCounts(view, hiddenRowIds, showRowGapMarkers),
    [view, hiddenRowIds, showRowGapMarkers],
  );

  const columnName = useCallback(
    (columnId: ColumnId) => {
      const column = view.columns.find((item) => item.id === columnId);
      return column ? column.label || column.letter : "";
    },
    [view.columns],
  );

  /**
   * The insertion line and the name plate are moved by writing a transform,
   * never by a render. Frozen cells are painted at their own offset because
   * they are sticky; everything past the seam has scrolled by scrollLeft.
   */
  const paintOverlay = useCallback(
    (insertion: number, clientX: number) => {
      const drag = dragRef.current;
      const line = indicatorRef.current;
      const chip = chipRef.current;
      if (!drag || !line || !chip) return;
      const k = Math.min(Math.max(insertion, 0), view.columns.length);
      const seam = ROW_INDEX_WIDTH + geometry.offsets[frozen];
      const raw =
        k <= frozen
          ? ROW_INDEX_WIDTH + geometry.offsets[k]
          : ROW_INDEX_WIDTH + geometry.offsets[k] - scrollLeftRef.current;
      // Never let the line slide under the frozen block or the gutter: the seam
      // is the honest place to say "this lands at the start of the scrolling
      // columns".
      const x = Math.max(raw, k <= frozen ? ROW_INDEX_WIDTH : seam);
      line.style.transform = `translateX(${x - INDICATOR_WIDTH / 2}px)`;
      const chipX = Math.min(
        Math.max(clientX - drag.rootLeft, ROW_INDEX_WIDTH),
        Math.max(drag.rootWidth - 8, ROW_INDEX_WIDTH),
      );
      chip.style.transform = `translateX(${chipX}px)`;
    },
    [geometry, frozen, view.columns.length],
  );

  const updateDropTarget = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      const insertion = insertionIndexAt(
        geometry,
        frozen,
        scrollLeftRef.current,
        clientX - drag.rootLeft,
      );
      dropIndexRef.current = insertion;
      paintOverlay(insertion, clientX);
    },
    [geometry, frozen, paintOverlay],
  );

  const autoScrollTick = useCallback(() => {
    autoScrollFrameRef.current = null;
    const body = bodyRef.current;
    const step = autoScrollStepRef.current;
    if (!dragRef.current || !body || step === 0) return;
    const before = body.scrollLeft;
    body.scrollLeft = before + step;
    // The scroll event arrives on its own schedule and the drop target cannot
    // wait for it, so the mirror handleScrollCapture would do is made here too.
    scrollLeftRef.current = body.scrollLeft;
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = body.scrollLeft;
    }
    // The pointer is standing still while the content moves under it, so the
    // drop target has to be recomputed from the last pointer x.
    if (body.scrollLeft !== before) updateDropTarget(pointerXRef.current);
    autoScrollFrameRef.current = window.requestAnimationFrame(autoScrollTick);
  }, [updateDropTarget]);

  const updateAutoScroll = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      const x = clientX - drag.rootLeft;
      // Left of the seam the pointer is over the gutter or the frozen block.
      // Neither scrolls, and both are a legal place to drop, so the natural
      // "scroll left" edge stops there rather than fighting the drop.
      const seam = ROW_INDEX_WIDTH + geometry.offsets[frozen];
      const ramp = (distance: number) =>
        Math.min(AUTO_SCROLL_MAX_STEP, Math.max(2, Math.round(distance / 3)));
      let step = 0;
      if (x >= seam && x < seam + AUTO_SCROLL_EDGE) {
        step = -ramp(seam + AUTO_SCROLL_EDGE - x);
      } else if (x > drag.rootWidth - AUTO_SCROLL_EDGE) {
        step = ramp(x - (drag.rootWidth - AUTO_SCROLL_EDGE));
      }
      autoScrollStepRef.current = step;
      if (step !== 0 && autoScrollFrameRef.current == null) {
        autoScrollFrameRef.current =
          window.requestAnimationFrame(autoScrollTick);
      }
    },
    [geometry, frozen, autoScrollTick],
  );

  /** The one teardown, idempotent, shared by every way a drag can end. */
  const endDrag = useCallback((pointerId?: number) => {
    const drag = dragRef.current;
    dragRef.current = null;
    autoScrollStepRef.current = 0;
    if (autoScrollFrameRef.current != null) {
      window.cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    const id = pointerId ?? drag?.pointerId;
    const root = rootRef.current;
    if (root && id != null && root.hasPointerCapture?.(id)) {
      root.releasePointerCapture(id);
    }
    setDragColumn(null);
  }, []);

  const handleHeaderPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, position: number) => {
      // Touch is out: below md the layout is cards with no header at all, and a
      // long press that fights the body's own scrolling is a second gesture to
      // get wrong. The Columns panel is the touch route.
      if (event.pointerType === "touch") return;
      if (!event.isPrimary || event.button !== 0) return;
      if (view.columns.length < 2) return;
      const target = event.target as HTMLElement | null;
      if (target && target.closest("[data-sheet-nodrag]")) return;
      const root = rootRef.current;
      if (!root) return;
      // Cleared on every press, so a suppressed click can never leak into the
      // next gesture: a click is always preceded by a pointerdown.
      suppressClickRef.current = false;
      const rect = root.getBoundingClientRect();
      pointerXRef.current = event.clientX;
      dragRef.current = {
        columnId: view.columns[position].id,
        fromPosition: position,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        rootLeft: rect.left,
        rootWidth: rect.width,
        armed: false,
      };
      // Deliberately no preventDefault and no pointer capture yet. Capture
      // retargets the compatibility click onto the capture element, and a press
      // that turns out to be a plain click still has to reach the sort button.
    },
    [view.columns],
  );

  const handleRootPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      pointerXRef.current = event.clientX;
      if (!drag.armed) {
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
        drag.armed = true;
        suppressClickRef.current = true;
        // Capture on the root, not on the cell: a wide sheet can scroll the
        // dragged column out of the rendered window, and a capture element that
        // leaves the document takes the capture with it.
        rootRef.current?.setPointerCapture?.(event.pointerId);
        setDragColumn(drag.columnId);
        setAnnouncement(
          `Moving ${columnName(drag.columnId)}, position ` +
            `${drag.fromPosition + 1} of ${view.columns.length}.`,
        );
      }
      updateDropTarget(event.clientX);
      updateAutoScroll(event.clientX);
    },
    [columnName, updateDropTarget, updateAutoScroll, view.columns.length],
  );

  const handleRootPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      const { armed, fromPosition, columnId } = drag;
      const insertion = dropIndexRef.current;
      endDrag(event.pointerId);
      // A press that never armed is a click - let it sort.
      if (!armed) return;
      // The two insertions that mean "where it already is": its own gap, and
      // the gap just past it.
      if (insertion === fromPosition || insertion === fromPosition + 1) return;
      const before = view.columns[insertion];
      onMoveColumn(columnId, before ? before.id : null);
      const landing = insertion > fromPosition ? insertion : insertion + 1;
      setAnnouncement(
        `${columnName(columnId)} moved to position ${landing} of ` +
          `${view.columns.length}.`,
      );
    },
    [endDrag, onMoveColumn, columnName, view.columns],
  );

  const handleRootPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      suppressClickRef.current = true;
      endDrag(event.pointerId);
    },
    [endDrag],
  );

  const handleRootClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Belt to the pointer capture's braces, for a browser that does not
      // retarget the compatibility click onto the capture element.
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  /** One place on the keyboard, and the same one the Columns panel uses. */
  const moveColumnByOne = useCallback(
    (position: number, delta: number) => {
      const target = position + delta;
      if (target < 0 || target >= view.columns.length) return;
      const column = view.columns[position];
      const before =
        delta > 0 ? view.columns[target + 1] : view.columns[target];
      onMoveColumn(column.id, before ? before.id : null);
      setAnnouncement(
        `${column.label || column.letter} moved to position ${target + 1} ` +
          `of ${view.columns.length}.`,
      );
    },
    [view.columns, onMoveColumn],
  );

  const handleHeaderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, position: number) => {
      if (!event.shiftKey || !(event.ctrlKey || event.metaKey)) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      event.stopPropagation();
      moveColumnByOne(position, event.key === "ArrowLeft" ? -1 : 1);
    },
    [moveColumnByOne],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !dragRef.current) return;
      // A window listener in the capture phase runs ahead of the sheet's own
      // document-capture ladder, so Escape aborts the drag and nothing else -
      // without this it would clear the cell selection on its way past.
      event.stopPropagation();
      event.preventDefault();
      suppressClickRef.current = true;
      endDrag();
      setAnnouncement("Move cancelled.");
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [endDrag]);

  // An undo, a redo or a sheet switch landing mid-drag changes the columns
  // under the gesture; there is nothing left to drop onto.
  useEffect(() => {
    if (dragRef.current) endDrag();
  }, [view.columns, endDrag]);

  const recomputeRange = useCallback(
    (width: number) => {
      const next = visibleColumnRange(
        geometry.offsets,
        scrollLeftRef.current,
        width,
        2,
        frozen,
      );
      setRange((current) =>
        current.first === next.first && current.last === next.last
          ? current
          : next,
      );
    },
    [geometry.offsets, frozen],
  );

  useEffect(() => {
    recomputeRange(viewportWidth);
  }, [recomputeRange, viewportWidth]);

  /**
   * The header follows the body by writing scrollLeft directly, and React state
   * changes only when a different set of columns comes into view - putting the
   * offset itself in state re-rendered the whole table on every scroll frame.
   *
   * The listener is onScrollCapture on an ancestor rather than a listener on
   * the scroller: a scroll event does not bubble, but it does capture, and the
   * scroller itself is created by react-window.
   */
  const handleScrollCapture = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const element = event.target as HTMLElement | null;
      if (!element || typeof element.scrollLeft !== "number") return;
      bodyRef.current = element as HTMLDivElement;
      scrollLeftRef.current = element.scrollLeft;
      if (headerScrollRef.current) {
        headerScrollRef.current.scrollLeft = element.scrollLeft;
      }
      // A wheel scroll mid-drag moves the columns under a pointer that has not
      // moved, so the insertion point has to be read again.
      if (dragRef.current) updateDropTarget(pointerXRef.current);
      if (frameRef.current != null) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        recomputeRange(element.clientWidth || viewportWidth);
      });
    },
    [recomputeRange, viewportWidth, updateDropTarget],
  );

  useEffect(() => {
    return () => {
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (autoScrollFrameRef.current != null) {
        window.cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
      }
    };
  }, []);

  const handleCellDown = useCallback(
    (rowPos: number, colPos: number, extend: boolean) => {
      onSelectionChange(
        extend && selection
          ? { ...selection, focusRow: rowPos, focusCol: colPos }
          : {
              kind: "cells",
              anchorRow: rowPos,
              anchorCol: colPos,
              focusRow: rowPos,
              focusCol: colPos,
            },
      );
    },
    [onSelectionChange, selection],
  );

  const handleCellEnter = useCallback(
    (rowPos: number, colPos: number, buttons: number) => {
      if (buttons !== 1 || !selection) return;
      // Dragging out of the gutter keeps a row selection a row selection: it
      // used to turn into a two-column block, and then Hide rows acted on that.
      onSelectionChange(
        selection.kind === "rows"
          ? { ...selection, focusRow: rowPos }
          : { ...selection, focusRow: rowPos, focusCol: colPos },
      );
    },
    [onSelectionChange, selection],
  );

  const handleGutterDown = useCallback(
    (rowPos: number, extend: boolean) => {
      const lastColumn = Math.max(0, view.columns.length - 1);
      onSelectionChange(
        extend && selection
          ? {
              ...selection,
              kind: "rows",
              anchorCol: 0,
              focusRow: rowPos,
              focusCol: lastColumn,
            }
          : {
              kind: "rows",
              anchorRow: rowPos,
              anchorCol: 0,
              focusRow: rowPos,
              focusCol: lastColumn,
            },
      );
    },
    [onSelectionChange, selection, view.columns.length],
  );

  const handleRevealRowsBefore = useCallback(
    (rowPos: number) => {
      const upper = view.rows[rowPos].sourceIndex;
      const lower = rowPos > 0 ? view.rows[rowPos - 1].sourceIndex : -1;
      const ids = hiddenRowIds.filter((id) => id > lower && id < upper);
      if (ids.length > 0) onRevealRows(ids);
    },
    [hiddenRowIds, onRevealRows, view.rows],
  );

  const itemData = useMemo<RowItemData>(
    () => ({
      rows: view.rows,
      columns: view.columns,
      widths: geometry.widths,
      offsets: geometry.offsets,
      totalWidth: geometry.totalWidth,
      range,
      hiddenBefore,
      frozenCount: frozen,
      hiddenRowsBefore,
      query,
      activeMatch,
      selection,
      onCellDown: handleCellDown,
      onCellEnter: handleCellEnter,
      onGutterDown: handleGutterDown,
      onRevealRowsBefore: handleRevealRowsBefore,
      onCellExpand,
    }),
    [
      view.rows,
      view.columns,
      geometry,
      range,
      hiddenBefore,
      frozen,
      hiddenRowsBefore,
      query,
      activeMatch,
      selection,
      handleCellDown,
      handleCellEnter,
      handleGutterDown,
      handleRevealRowsBefore,
      onCellExpand,
    ],
  );

  const sortIndexOf = useCallback(
    (columnId: ColumnId) =>
      sort.findIndex((rule) => rule.columnId === columnId),
    [sort],
  );

  const handleResizeDown = useCallback(
    (
      event: React.PointerEvent<HTMLDivElement>,
      columnId: ColumnId,
      width: number,
    ) => {
      // The header is a sort button and holds the menu caret, so the handle has
      // to keep its gesture to itself.
      event.preventDefault();
      event.stopPropagation();
      (event.currentTarget as HTMLDivElement).setPointerCapture?.(
        event.pointerId,
      );
      resizeRef.current = {
        columnId,
        startX: event.clientX,
        startWidth: width,
      };
      setResizeDraft({ columnId, width });
    },
    [],
  );

  const handleResizeMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = resizeRef.current;
      if (!start) return;
      const next = Math.min(
        Math.max(
          start.startWidth + (event.clientX - start.startX),
          MIN_COLUMN_WIDTH,
        ),
        MAX_RESIZE_WIDTH,
      );
      setResizeDraft({ columnId: start.columnId, width: next });
    },
    [],
  );

  const handleResizeUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const start = resizeRef.current;
      if (!start) return;
      (event.currentTarget as HTMLDivElement).releasePointerCapture?.(
        event.pointerId,
      );
      resizeRef.current = null;
      const width = Math.min(
        Math.max(
          start.startWidth + (event.clientX - start.startX),
          MIN_COLUMN_WIDTH,
        ),
        MAX_RESIZE_WIDTH,
      );
      setResizeDraft(null);
      onColumnWidthChange(start.columnId, width);
    },
    [onColumnWidthChange],
  );

  const renderHeaderCell = (position: number, isFrozen: boolean) => {
    const column = view.columns[position];
    const sortIndex = sortIndexOf(column.id);
    const rule = sortIndex >= 0 ? sort[sortIndex] : null;
    const hiddenCount = hiddenBefore[position];
    const width = geometry.widths[position];

    return (
      <Box
        key={column.id}
        role="columnheader"
        // The header row holds spacers as well as cells, so its DOM order has
        // never matched the column index.
        aria-colindex={position + 1}
        aria-sort={
          rule
            ? rule.direction === "asc"
              ? "ascending"
              : "descending"
            : "none"
        }
        onPointerDown={(event: React.PointerEvent<HTMLDivElement>) =>
          handleHeaderPointerDown(event, position)
        }
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
          handleHeaderKeyDown(event, position)
        }
        sx={{
          ...HEADER_CELL_BASE,
          width,
          ...(dragColumn === column.id
            ? { opacity: 0.4, backgroundColor: "action.selected" }
            : {}),
          borderRight:
            isFrozen && position === frozen - 1
              ? "2px solid"
              : HEADER_CELL_BASE.borderRight,
          borderRightColor:
            isFrozen && position === frozen - 1 ? "grey.400" : "divider",
          ...(isFrozen
            ? {
                position: "sticky",
                left: ROW_INDEX_WIDTH + geometry.offsets[position],
                zIndex: 3,
                // A sticky header cell has scrolling headers passing under it.
                backgroundColor: (t) => t.palette.grey[100],
              }
            : {}),
        }}
      >
        {hiddenCount > 0 && (
          <Tooltip
            title={`${hiddenCount} hidden column${hiddenCount === 1 ? "" : "s"}. Click to show.`}
          >
            <Box
              component="span"
              role="button"
              data-sheet-nodrag
              aria-label={`Show ${hiddenCount} hidden columns`}
              onClick={() => onRevealColumnsBefore(column.id)}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 6,
                cursor: "pointer",
                borderLeft: "3px double",
                borderColor: "warning.main",
              }}
            />
          </Tooltip>
        )}
        <Tooltip
          // Empty while a drag is on: MUI renders no tooltip for an empty
          // title and closes an open one, where toggling `open` would trip its
          // controlled/uncontrolled warning.
          title={
            dragColumn
              ? ""
              : "Drag to move this column. Click to sort; Shift+click adds to the sort."
          }
        >
          <Box
            component="button"
            type="button"
            aria-describedby={REORDER_HINT_ID}
            onClick={(event: React.MouseEvent) =>
              onSortColumn(column.id, event.shiftKey)
            }
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "baseline",
              gap: 0.5,
              border: "none",
              background: "none",
              p: 0,
              font: "inherit",
              color: "inherit",
              // Inherits grab from the cell: the button is the drag surface.
              cursor: "inherit",
              textAlign: "left",
              overflow: "hidden",
            }}
          >
            <Box
              component="span"
              sx={{ color: "text.disabled", fontWeight: 500, fontSize: 11 }}
            >
              {column.letter}
            </Box>
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={column.label || column.letter}
            >
              {column.label ? (
                query ? (
                  <HighlightedText text={column.label} query={query} />
                ) : (
                  column.label
                )
              ) : (
                ""
              )}
            </Box>
          </Box>
        </Tooltip>
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            opacity: rule ? 1 : 0.25,
            fontSize: 11,
          }}
        >
          {rule && rule.direction === "desc" ? (
            <ArrowDownIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowUpIcon sx={{ fontSize: 14 }} />
          )}
          {sort.length > 1 && sortIndex >= 0 && (
            <Box component="sup" sx={{ fontSize: 9 }}>
              {sortIndex + 1}
            </Box>
          )}
        </Box>
        <IconButton
          size="small"
          data-sheet-nodrag
          aria-haspopup="menu"
          aria-label={`Column options: ${column.label || column.letter}`}
          onClick={(event) => onOpenColumnMenu(column.id, event.currentTarget)}
          sx={{ p: 0.25 }}
        >
          <MenuArrowIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Tooltip title="Drag to resize. Double click to fit the contents.">
          <Box
            data-sheet-nodrag
            aria-label={`Resize column ${column.label || column.letter}`}
            onPointerDown={(event: React.PointerEvent<HTMLDivElement>) =>
              handleResizeDown(event, column.id, width)
            }
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeUp}
            onDoubleClick={(event: React.MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              onAutoFitColumn(column.id);
            }}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: 0,
              width: RESIZE_HANDLE_WIDTH,
              cursor: "col-resize",
              touchAction: "none",
              zIndex: 4,
              "&:hover": { backgroundColor: "primary.light", opacity: 0.6 },
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const frozenWidth = geometry.offsets[frozen] + ROW_INDEX_WIDTH;
  const frozenCrowdsViewport =
    frozen > 0 && viewportWidth > 0 && frozenWidth > viewportWidth * 0.6;

  const windowFirst = Math.max(frozen, range.first);
  const windowLast = Math.min(view.columns.length - 1, range.last);
  const leadWidth = geometry.offsets[windowFirst] - geometry.offsets[frozen];

  return (
    <Box
      ref={rootRef}
      role="grid"
      aria-rowcount={view.rows.length}
      aria-colcount={view.columns.length}
      onPointerMove={handleRootPointerMove}
      onPointerUp={handleRootPointerUp}
      onPointerCancel={handleRootPointerCancel}
      onLostPointerCapture={handleRootPointerCancel}
      onClickCapture={handleRootClickCapture}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        // The overlay is positioned against this box, and both of its nodes
        // paint above the body's own stacking context because they come after
        // it in DOM order.
        position: "relative",
        ...(dragColumn
          ? { cursor: "grabbing", userSelect: "none" as const }
          : {}),
      }}
    >
      {frozenCrowdsViewport && (
        <Box
          sx={{
            px: 1.5,
            py: 0.25,
            fontSize: 12,
            color: "warning.dark",
            backgroundColor: "warning.light",
            flexShrink: 0,
          }}
        >
          Frozen columns take most of the width - there is little room left to
          scroll.
        </Box>
      )}
      <Box
        ref={headerScrollRef}
        sx={{
          overflow: "hidden",
          borderBottom: "2px solid",
          borderColor: "divider",
          backgroundColor: (t) => t.palette.grey[100],
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", width: geometry.totalWidth }} role="row">
          <Box
            sx={{
              width: ROW_INDEX_WIDTH,
              flexShrink: 0,
              p: "6px 8px",
              fontSize: 12,
              fontWeight: 700,
              textAlign: "center",
              borderRight: "1px solid",
              borderColor: "divider",
              backgroundColor: (t) => t.palette.grey[200],
              position: "sticky",
              left: 0,
              zIndex: 4,
              boxSizing: "border-box",
            }}
            title="Row numbers are the row numbers in the original file"
          />
          {view.columns
            .slice(0, frozen)
            .map((_, position) => renderHeaderCell(position, true))}
          {leadWidth > 0 && <Box sx={{ width: leadWidth, flexShrink: 0 }} />}
          {view.columns
            .slice(windowFirst, windowLast + 1)
            .map((_, offset) => renderHeaderCell(windowFirst + offset, false))}
        </Box>
      </Box>

      <Box
        onScrollCapture={handleScrollCapture}
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          "& > div": {
            "&::-webkit-scrollbar": { width: 10, height: 10 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.25)",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.4)",
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          },
        }}
      >
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => {
            if (width !== viewportWidth) {
              // AutoSizer calls this during render; defer the state change.
              window.setTimeout(() => setViewportWidth(width), 0);
            }
            return (
              <FixedSizeList
                ref={(node) => {
                  listRef.current = node;
                }}
                // The scroll handler only learns about this element after the
                // first scroll, and auto-scrolling a drag cannot wait for one.
                outerRef={(node: HTMLDivElement | null) => {
                  bodyRef.current = node;
                }}
                height={height}
                width={width}
                itemCount={view.rows.length}
                itemSize={ROW_HEIGHT}
                itemData={itemData}
                overscanCount={8}
              >
                {SheetGridRow}
              </FixedSizeList>
            );
          }}
        </AutoSizer>
      </Box>

      {/* Always mounted: the overlay is painted from inside the pointermove
          that arms the drag, before React has committed anything. */}
      <Box
        ref={indicatorRef}
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: INDICATOR_WIDTH,
          backgroundColor: "primary.main",
          pointerEvents: "none",
          zIndex: 6,
          willChange: "transform",
          display: dragColumn ? "block" : "none",
        }}
      />
      <Box
        ref={chipRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: ROW_HEIGHT,
          alignItems: "center",
          px: 1,
          fontSize: 12,
          fontWeight: 700,
          color: "primary.contrastText",
          backgroundColor: "primary.main",
          borderRadius: 1,
          whiteSpace: "nowrap",
          pointerEvents: "none",
          opacity: 0.95,
          zIndex: 6,
          willChange: "transform",
          display: dragColumn ? "flex" : "none",
        }}
      >
        {dragColumn ? columnName(dragColumn) : ""}
      </Box>
      <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>
        {announcement}
      </Box>
      <Box component="span" id={REORDER_HINT_ID} sx={SR_ONLY_SX}>
        Press Control plus Shift plus Left or Right Arrow to move this column.
      </Box>
    </Box>
  );
};

export default SheetGrid;
